/**
 * Admin editor for a single Inspiration article (/admin/ideas/:id).
 *
 * Editable: title, period (year/month), TipTap content (autosaved), attachments
 * (upload + delete), publish toggle, delete article.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  CheckCircle,
  CircleDashed,
  FloppyDisk,
  Paperclip,
  Trash,
  UploadSimple,
  DownloadSimple,
} from "@phosphor-icons/react";
import { inspirationsApi, inspirationsAdminApi } from "../../api/inspirations";
import { MONTH_KEYS, monthLabel, now } from "../../lib/dates";
import ChapterRichEditor from "../../components/courses/ChapterRichEditor";
import ConfirmDialog from "../../components/courses/ConfirmDialog";
import FileIcon from "../../components/resources/FileIcon";
import { fileKind, humanFileSize } from "../../api/resources";

const AUTOSAVE_MS = 1200;

export default function AdminInspirationEditor() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const cur = useMemo(() => now(), []);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [content, setContent] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [titleEditing, setTitleEditing] = useState(false);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);

  const yearOptions = useMemo(() => {
    const list = [];
    for (let y = cur.year + 1; y >= cur.year - 4; y--) list.push(y);
    return list;
  }, [cur.year]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await inspirationsApi.get(id);
      setArticle(res);
      setContent(res.content || { type: "doc", content: [] });
      setTitleDraft(res.title);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const persistContent = useCallback(
    async (next) => {
      if (!article) return;
      setSaveStatus("saving");
      try {
        await inspirationsAdminApi.update(article.id, { content: next });
        setSaveStatus("saved");
        setSavedAt(new Date());
      } catch {
        setSaveStatus("error");
      }
    },
    [article],
  );

  const onContentChange = useCallback(
    (next) => {
      setContent(next);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => persistContent(next), AUTOSAVE_MS);
    },
    [persistContent],
  );

  const saveTitle = useCallback(async () => {
    if (!article) return;
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === article.title) {
      setTitleEditing(false);
      setTitleDraft(article.title);
      return;
    }
    const updated = await inspirationsAdminApi.update(article.id, {
      title: trimmed,
    });
    setArticle(updated);
    setTitleEditing(false);
  }, [article, titleDraft]);

  const setPeriod = useCallback(
    async (year, month) => {
      if (!article) return;
      const updated = await inspirationsAdminApi.update(article.id, {
        year,
        month,
      });
      setArticle(updated);
    },
    [article],
  );

  const togglePublish = useCallback(async () => {
    if (!article) return;
    const next = article.status === "published" ? "draft" : "published";
    const updated = await inspirationsAdminApi.update(article.id, {
      status: next,
    });
    setArticle(updated);
  }, [article]);

  const handleUpload = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || !article) return;
      try {
        await inspirationsAdminApi.uploadAttachment(article.id, file);
        await load();
      } catch (err) {
        console.error(err);
        alert(t("inspiration.upload_error"));
      }
    },
    [article, load, t],
  );

  const removeAttachment = useCallback(
    async (attId) => {
      if (!article) return;
      await inspirationsAdminApi.removeAttachment(article.id, attId);
      await load();
    },
    [article, load],
  );

  const onDelete = useCallback(async () => {
    if (!article) return;
    await inspirationsAdminApi.remove(article.id);
    setConfirmDelete(false);
    navigate("/admin/ideas");
  }, [article, navigate]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (loading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (error || !article) {
    return <p className="text-sm text-red-600">{t("inspiration.not_found")}</p>;
  }
  const isPublished = article.status === "published";

  return (
    <div data-testid="admin-inspiration-editor-page" className="space-y-6">
      <Link
        to="/admin/ideas"
        data-testid="admin-inspiration-back"
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} weight="bold" />
        {t("admin.nav.ideas")}
      </Link>

      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            {t("inspiration.article_kicker")}
          </p>
          {titleEditing ? (
            <input
              autoFocus
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveTitle();
                if (e.key === "Escape") {
                  setTitleEditing(false);
                  setTitleDraft(article.title);
                }
              }}
              data-testid="admin-inspiration-title-input"
              className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 bg-transparent outline-none border-b border-slate-300 w-full"
            />
          ) : (
            <h1
              onClick={() => setTitleEditing(true)}
              data-testid="admin-inspiration-title"
              className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 cursor-text"
            >
              {article.title}
            </h1>
          )}

          {/* Period selector */}
          <div
            data-testid="admin-inspiration-period"
            className="mt-4 flex items-center gap-2 text-sm text-slate-600"
          >
            <select
              data-testid="admin-inspiration-period-month"
              value={article.month}
              onChange={(e) => setPeriod(article.year, parseInt(e.target.value, 10))}
              className="bg-transparent border border-slate-200 rounded-full px-3 py-1 capitalize"
            >
              {MONTH_KEYS.map((k, i) => (
                <option key={i} value={i + 1}>
                  {t(k)}
                </option>
              ))}
            </select>
            <select
              data-testid="admin-inspiration-period-year"
              value={article.year}
              onChange={(e) => setPeriod(parseInt(e.target.value, 10), article.month)}
              className="bg-transparent border border-slate-200 rounded-full px-3 py-1"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <span
            data-testid="admin-inspiration-save-status"
            className="text-xs text-slate-500 inline-flex items-center gap-1.5"
          >
            {saveStatus === "saving" && (
              <>
                <FloppyDisk size={14} className="animate-pulse" />
                {t("admin.courses.saving")}
              </>
            )}
            {saveStatus === "saved" && savedAt && (
              <>
                <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                {t("admin.courses.saved_at", {
                  time: savedAt.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                })}
              </>
            )}
            {saveStatus === "error" && (
              <span className="text-red-600">{t("admin.courses.save_error")}</span>
            )}
          </span>

          <button
            type="button"
            onClick={togglePublish}
            data-testid="admin-inspiration-toggle-publish"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-all ${
              isPublished
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-amber-50 text-amber-700 hover:bg-amber-100"
            }`}
          >
            {isPublished ? (
              <>
                <CheckCircle size={14} weight="fill" />
                {t("inspiration.status_published")}
              </>
            ) : (
              <>
                <CircleDashed size={14} weight="bold" />
                {t("inspiration.status_draft")}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            data-testid="admin-inspiration-delete"
            aria-label={t("common.delete")}
            className="inline-flex items-center justify-center rounded-full bg-red-50 text-red-600 hover:bg-red-100 h-9 w-9"
          >
            <Trash size={14} weight="bold" />
          </button>
        </div>
      </header>

      <ChapterRichEditor
        value={content}
        onChange={onContentChange}
        placeholder={t("inspiration.editor_placeholder")}
      />

      {/* Attachments */}
      <section data-testid="admin-inspiration-attachments" className="space-y-3">
        <div className="flex items-center gap-3">
          <h2 className="font-heading text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Paperclip size={16} weight="bold" />
            {t("inspiration.attachments")}
          </h2>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="admin-inspiration-attach-button"
            className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
          >
            <UploadSimple size={13} weight="bold" />
            {t("inspiration.attach_file")}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleUpload}
            data-testid="admin-inspiration-attach-input"
          />
        </div>

        {article.attachments.length === 0 ? (
          <p className="text-xs text-slate-400">
            {t("inspiration.no_attachment")}
          </p>
        ) : (
          <div className="space-y-2">
            {article.attachments.map((att) => {
              const kind = fileKind(att.mime_type, att.original_filename);
              return (
                <div
                  key={att.id}
                  data-testid={`admin-inspiration-attachment-${att.id}`}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                    <FileIcon kind={kind} size={18} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {att.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {humanFileSize(att.size)}
                    </p>
                  </div>
                  <a
                    href={inspirationsApi.attachmentDownloadUrl(att.id)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
                  >
                    <DownloadSimple size={13} weight="bold" />
                    {t("resources.download")}
                  </a>
                  <button
                    type="button"
                    onClick={() => removeAttachment(att.id)}
                    aria-label={t("common.delete")}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-50 text-red-600"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ConfirmDialog
        open={confirmDelete}
        title={t("inspiration.delete_article_title")}
        description={t("inspiration.delete_article_desc")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        destructive
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
